import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import LineMap from './LineMap';
import { STATIONS, StationId, ZONE_LABELS, LINE_LABELS, stationIndex } from './lineModel';

/**
 * "How EU food law is made": a walk along the metro line, one station at a
 * time, with what happens there in metro terms and in Brussels terms.
 * Rendered in a portal on document.body, so no transformed ancestor (the
 * page entrance animations) can trap the fixed backdrop.
 */

interface ExplainerProps {
  onClose: () => void;
  /** Station to open at (a dossier's current one); defaults to the first. */
  start?: StationId | null;
  /** Show the closing link to /legislation/pipeline (off on that page itself). */
  linkToPipeline?: boolean;
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function LegislationExplainer({ onClose, start, linkToPipeline = true }: ExplainerProps) {
  const [current, setCurrent] = useState<StationId>(start ?? 'feedback');
  const dialogRef = useRef<HTMLDivElement>(null);
  const idx = stationIndex(current);
  const station = STATIONS[idx];
  const isLast = idx === STATIONS.length - 1;

  const back = useCallback(() => setCurrent(STATIONS[Math.max(0, idx - 1)].id), [idx]);
  const next = useCallback(() => setCurrent(STATIONS[Math.min(STATIONS.length - 1, idx + 1)].id), [idx]);

  // Lock the page scroll, take focus, and give it back on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, []);

  // Keys on the whole document: the button that had focus can disappear
  // (Next is swapped for the closing link at the last station).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && !e.altKey && !e.ctrlKey && !e.metaKey) {
        // Alt+Left is the browser's Back: leave modified arrows alone.
        if (e.key === 'ArrowRight') next(); else back();
      } else if (e.key === 'Tab') {
        // Keep Tab inside the dialog; the hidden map drawing has no visible buttons.
        const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
          .filter(el => el.offsetParent !== null);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!dialog.contains(active)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && (active === first || active === dialog)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [next, back, onClose]);

  // A removed button leaves focus on the page: bring it back to the dialog.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
  }, [idx]);

  // Close on a click that both starts and ends on the backdrop, not on a text
  // selection that ends there.
  const pressedBackdrop = useRef(false);

  const closeButton = (
    <button type="button" className="legis-explainer__btn legis-explainer__btn--primary" onClick={onClose}>
      See what is on the line today
    </button>
  );

  return createPortal(
    <div
      className="modal-backdrop legis-explainer__backdrop"
      onMouseDown={e => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => {
        e.stopPropagation();
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose();
        pressedBackdrop.current = false;
      }}
    >
      <div
        ref={dialogRef}
        className="modal legis-explainer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legis-explainer-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <header className="legis-explainer__header">
          <div>
            <p className="legis-explainer__eyebrow">How EU food law is made</p>
            <h2 id="legis-explainer-title" className="legis-explainer__title">Every draft rides the same line</h2>
            <p className="legis-explainer__intro">
              Two lines share one track. Line S (pesticide MRLs, food contact materials, microbiological
              criteria) stops for scrutiny by Parliament and Council; Line E (feed additives, import
              conditions, official controls) runs straight to adoption.
            </p>
          </div>
          <button type="button" className="legis-explainer__close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="legis-explainer__body">
          <LineMap variant="explain" legend selected={current} onSelect={setCurrent} label="Stations of the legislative process" />

          {/* Stays mounted, so screen readers announce each step (the cards below remount). */}
          <p className="sr-only" aria-live="polite">
            Station {idx + 1} of {STATIONS.length}: {station.name}. {station.metro}.
          </p>
          <div className="legis-explainer__cards" key={current}>
            <section className="legis-explainer__card legis-explainer__card--metro">
              <div className="legis-explainer__card-head">
                <span className="legis-explainer__eyebrow">On the line</span>
                <span className="legis-explainer__lines">
                  {station.lines.map(line => (
                    <span key={line} className={`line-chip line-chip--${line.toLowerCase()}`}>{LINE_LABELS[line].name}</span>
                  ))}
                </span>
              </div>
              <h3>{station.metro}</h3>
              <p>{station.metroText}</p>
            </section>
            <section className="legis-explainer__card legis-explainer__card--real">
              <span className="legis-explainer__eyebrow">In Brussels</span>
              <h3>{station.realTitle}</h3>
              <p>{station.realText}</p>
            </section>
          </div>

          <dl className="legis-explainer__facts">
            <div><dt>Who decides</dt><dd>{station.who}</dd></div>
            <div><dt>How long</dt><dd>{station.time}</dd></div>
            <div><dt>Can the text still change?</dt><dd className={`legis-explainer__change--${station.changeTone}`}>{station.change}</dd></div>
            <div>
              <dt>Fare zone</dt>
              <dd><span className={`legis-explainer__zone legis-explainer__zone--${station.zone}`}>Zone {station.zone + 1} · {ZONE_LABELS[station.zone].toLowerCase()}</span></dd>
            </div>
          </dl>
        </div>

        <footer className="legis-explainer__footer">
          <div className="legis-explainer__progress">
            <span>Station {idx + 1} of {STATIONS.length}</span>
            <span className="legis-explainer__dots" aria-hidden="true">
              {STATIONS.map((s, i) => (
                <span key={s.id} className={`legis-explainer__dot ${i === idx ? 'legis-explainer__dot--on' : i < idx ? 'legis-explainer__dot--done' : ''}`} />
              ))}
            </span>
          </div>
          <div className="legis-explainer__nav">
            <button type="button" className="legis-explainer__btn" onClick={back} disabled={idx === 0}>Back</button>
            {!isLast && (
              <button type="button" className="legis-explainer__btn legis-explainer__btn--primary" onClick={next}>Next station</button>
            )}
            {isLast && (linkToPipeline ? (
              <Link to="/legislation/pipeline" className="legis-explainer__btn legis-explainer__btn--primary" onClick={onClose}>
                See what is on the line today
              </Link>
            ) : closeButton)}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/** A button that opens the explainer, at a dossier's station when given one. */
export function ExplainerButton({ station, linkToPipeline = true, className, children }: {
  station?: StationId | null;
  linkToPipeline?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className ?? 'legis-explainer-link'} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        {children ?? 'How does EU food law get made?'}
      </button>
      {open && <LegislationExplainer start={station} linkToPipeline={linkToPipeline} onClose={() => setOpen(false)} />}
    </>
  );
}
